import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { Iconify } from 'src/components/iconify';
import { toast } from 'src/components/snackbar';
import { getErrorDescription } from 'src/utils/error-message';
import { MonacoJsonEditor } from 'src/components/monaco-json-editor';

function isJsonInvalid(value) {
  if (!value?.trim()) return false;
  try {
    JSON.parse(value);
    return false;
  } catch {
    return true;
  }
}

export function JsonField({
  label,
  value,
  onChange,
  onValidityChange,
  readOnly = false,
  minRows = 10,
  maxHeight,
  wrap = false,
  hideActions = false,
  hideLineNumbers = false,
  square = false,
  // Extra icon buttons for the header, shown before the built-in copy/format ones.
  actions,
}) {
  const invalid = isJsonInvalid(value);
  const showHeader = Boolean(label) || Boolean(actions) || !hideActions;
  const editorOptions = hideLineNumbers
    ? { lineNumbers: 'off', lineDecorationsWidth: 0, folding: false, glyphMargin: false }
    : undefined;

  // Editing JSON is the one thing in this app that wants the whole window: on a 900px-tall
  // screen a pane in a card leaves ~450px for the document. Fullscreen lifts the field out over
  // the page and hands the editor the viewport. Monaco's `automaticLayout` re-measures on the way
  // in and out, so cursor and scroll survive the trip.
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    onValidityChange?.(invalid);
  }, [invalid, onValidityChange]);

  useEffect(() => {
    if (!fullscreen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setFullscreen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullscreen]);

  const handleFormat = () => {
    try {
      onChange(JSON.stringify(JSON.parse(value), null, 2));
    } catch (error) {
      toast.error('Invalid JSON — cannot format', { description: getErrorDescription(error) });
    }
  };

  const handleCopy = () => {
    try {
      navigator.clipboard?.writeText(value ?? '');
      toast.success('Copied');
    } catch {
      /* clipboard unavailable */
    }
  };

  // 38px of header, plus room for the invalid-JSON line when it's showing.
  const fullscreenHeight = invalid ? 'calc(100vh - 62px)' : 'calc(100vh - 38px)';

  const height = fullscreen ? fullscreenHeight : (maxHeight ?? `${Math.max(minRows, 6) * 19}px`);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: square ? 0 : 1.5,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        border: (theme) =>
          `solid 1px ${invalid ? theme.palette.error.main : theme.palette.divider}`,
        ...(fullscreen && {
          position: 'fixed',
          inset: 0,
          zIndex: (theme) => theme.zIndex.modal,
          borderRadius: 0,
          borderWidth: invalid ? 1 : 0,
        }),
      }}
    >
      {showHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            minHeight: 38,
            px: 1.5,
            py: 0.5,
            bgcolor: 'background.neutral',
            borderBottom: (theme) => `solid 1px ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2" noWrap>
            {label}
          </Typography>

          <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
            {actions}

            {!hideActions && (
              <>
                <Tooltip title="Copy">
                  <IconButton size="small" onClick={handleCopy}>
                    <Iconify icon="solar:copy-linear" width={16} />
                  </IconButton>
                </Tooltip>
                {!readOnly && (
                  <Tooltip title="Format JSON">
                    <IconButton size="small" onClick={handleFormat}>
                      <Iconify icon="solar:magic-stick-3-linear" width={16} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={fullscreen ? 'Exit full screen (Esc)' : 'Full screen'}>
                  <IconButton size="small" onClick={() => setFullscreen((prev) => !prev)}>
                    {fullscreen ? (
                      <FullscreenExitIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <FullscreenIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        </Box>
      )}

      <MonacoJsonEditor
        value={value ?? ''}
        onChange={onChange}
        readOnly={readOnly}
        height={height}
        wrap={wrap}
        options={editorOptions}
        sx={{ border: 'none', borderRadius: 0 }}
      />

      {invalid && (
        <Typography variant="caption" sx={{ color: 'error.main', px: 1.5, py: 0.5 }}>
          JSON is not valid
        </Typography>
      )}
    </Box>
  );
}
